import React, { useState } from 'react';
import { FaBullhorn, FaPlus, FaPercent, FaCalendarAlt, FaChartLine, FaPause, FaPlay } from 'react-icons/fa';

const SellerMarketing = () => {
    const [activeTab, setActiveTab] = useState('campaigns');

    const campaigns = [
        { id: 1, name: 'Diwali Sale', type: 'Discount', status: 'active', budget: 5000, spent: 3200, impressions: 12500, clicks: 450, startDate: '2024-10-15', endDate: '2024-11-15' },
        { id: 2, name: 'Winter Collection', type: 'Flash Sale', status: 'scheduled', budget: 3000, spent: 0, impressions: 0, clicks: 0, startDate: '2024-12-20', endDate: '2024-12-31' },
        { id: 3, name: 'Clearance Sale', type: 'BOGO', status: 'paused', budget: 2000, spent: 1500, impressions: 8000, clicks: 280, startDate: '2024-11-01', endDate: '2024-12-01' },
    ];

    const promotionTypes = [
        { type: 'Percentage Discount', description: 'Offer % off on products', icon: <FaPercent /> },
        { type: 'Flash Sale', description: 'Limited time steep discounts', icon: <FaBullhorn /> },
        { type: 'Buy One Get One', description: 'BOGO offers', icon: <FaPlus /> },
        { type: 'Free Shipping', description: 'Waive shipping fees', icon: <FaChartLine /> },
    ];

    const getStatusBadge = (status) => {
        const styles = {
            active: 'bg-green-100 text-green-800',
            scheduled: 'bg-blue-100 text-blue-800',
            paused: 'bg-yellow-100 text-yellow-800',
            ended: 'bg-gray-100 text-gray-800',
        };
        return styles[status] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Promotional Marketing</h1>
                <p className="text-gray-600">Create and manage marketing campaigns to boost sales</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 rounded-lg text-white">
                    <p className="text-purple-100 text-sm">Active Campaigns</p>
                    <p className="text-2xl font-bold">{campaigns.filter(c => c.status === 'active').length}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Total Budget</p>
                    <p className="text-2xl font-bold text-gray-800">₹{campaigns.reduce((sum, c) => sum + c.budget, 0).toLocaleString()}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Total Spent</p>
                    <p className="text-2xl font-bold text-gray-800">₹{campaigns.reduce((sum, c) => sum + c.spent, 0).toLocaleString()}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Total Clicks</p>
                    <p className="text-2xl font-bold text-gray-800">{campaigns.reduce((sum, c) => sum + c.clicks, 0).toLocaleString()}</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6">
                <button
                    onClick={() => setActiveTab('campaigns')}
                    className={`px-6 py-3 rounded-lg font-semibold transition ${activeTab === 'campaigns' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                >
                    My Campaigns
                </button>
                <button
                    onClick={() => setActiveTab('create')}
                    className={`px-6 py-3 rounded-lg font-semibold transition ${activeTab === 'create' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                >
                    Create Campaign
                </button>
            </div>

            {/* Campaigns Tab */}
            {activeTab === 'campaigns' && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Performance</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {campaigns.map((campaign) => (
                                <tr key={campaign.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{campaign.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{campaign.type}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(campaign.status)}`}>
                                            {campaign.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div>
                                            <span className="font-medium">₹{campaign.spent.toLocaleString()}</span>
                                            <span className="text-gray-400"> / ₹{campaign.budget.toLocaleString()}</span>
                                        </div>
                                        <div className="h-1.5 bg-gray-200 rounded-full mt-1 w-20">
                                            <div className="h-1.5 bg-blue-500 rounded-full" style={{ width: `${(campaign.spent / campaign.budget) * 100}%` }} />
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <div>{campaign.impressions.toLocaleString()} impressions</div>
                                        <div className="text-gray-500">{campaign.clicks} clicks</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {campaign.startDate} - {campaign.endDate}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button className={`p-2 rounded ${campaign.status === 'active' ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`}>
                                            {campaign.status === 'active' ? <FaPause /> : <FaPlay />}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create Campaign Tab */}
            {activeTab === 'create' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {promotionTypes.map((promo, index) => (
                        <div key={index} className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition cursor-pointer border-2 border-transparent hover:border-blue-500">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-2xl">
                                    {promo.icon}
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-800 text-lg">{promo.type}</h3>
                                    <p className="text-gray-500 text-sm">{promo.description}</p>
                                </div>
                            </div>
                            <button className="mt-4 w-full py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200">
                                Create Campaign
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SellerMarketing;
